import type { CreateSchemaKind } from '@cli-types/core-engine/commands.js'
import type { SchemaMetadata } from '@cli-types/next-adapter/commands.js'
import { CREATE_PROMPT_CONTEXT } from '@cli-constants/prompt-copy.js'
import { humanizeName, singularizeLabel } from '@cli-utils/text.js'
import { fail } from '../schema-prompts/fail.js'
import { promptText } from '../schema-prompts/prompt-text.js'
import { assertSchemaDoesNotExist } from './assert-schema-does-not-exist.js'
import { deriveSchemaNameFromLabel } from './derive-schema-name-from-label.js'
import { validateProvidedSchemaName } from './validate-provided-schema-name.js'
import { validateSchemaName } from './validate-schema-name.js'

export async function promptMetadata(
  kind: CreateSchemaKind,
  providedName: string | undefined,
  schemasDir: string
): Promise<SchemaMetadata> {
  if (providedName) {
    validateProvidedSchemaName(providedName)
    assertSchemaDoesNotExist(schemasDir, providedName)
  }

  const defaultLabel = providedName ? humanizeName(providedName) : undefined
  const label = await promptText(CREATE_PROMPT_CONTEXT, 'Schema label', defaultLabel)
  const name = providedName ?? deriveSchemaNameFromLabel(label)
  if (!providedName) {
    const error = validateSchemaName(name)
    if (error) {
      fail(`Schema label "${label}" derived invalid schema name "${name}". ${error}`)
    }
    assertSchemaDoesNotExist(schemasDir, name)
  }
  const displayNouns =
    kind === 'entity'
      ? {
          labelSingular: await promptText(
            CREATE_PROMPT_CONTEXT,
            'Singular noun',
            singularizeLabel(label)
          ),
          labelPlural: await promptText(CREATE_PROMPT_CONTEXT, 'Plural noun', label)
        }
      : {}
  const titleLabel = await promptText(CREATE_PROMPT_CONTEXT, 'First field label', {
    placeholder: 'Name'
  })

  return {
    name,
    label,
    ...displayNouns,
    description: kind === 'form' ? `${label} form` : `${label} schema`,
    icon: kind === 'form' ? 'Mail' : kind === 'single' ? 'Settings' : 'FileText',
    titleLabel
  }
}
